/*
 * Code generated by Speakeasy (https://speakeasy.com). DO NOT EDIT.
 */

import * as z from "zod/v4-mini";
import { remap as remap$ } from "../../lib/primitives.js";
import { safeParse } from "../../lib/schemas.js";
import { Result as SafeParseResult } from "../../types/fp.js";
import { SDKValidationError } from "../errors/sdkvalidationerror.js";
import {
  CanonicalToolAttributes,
  CanonicalToolAttributes$inboundSchema,
} from "./canonicaltoolattributes.js";
import {
  ToolAnnotations,
  ToolAnnotations$inboundSchema,
} from "./toolannotations.js";
import { ToolVariation, ToolVariation$inboundSchema } from "./toolvariation.js";

/**
 * A function tool
 */
export type FunctionToolDefinition = {
  /**
   * Tool annotations providing behavioral hints about the tool
   */
  annotations?: ToolAnnotations | undefined;
  /**
   * The ID of the asset
   */
  assetId: string;
  /**
   * The original details of a tool
   */
  canonical?: CanonicalToolAttributes | undefined;
  /**
   * The canonical name of the tool. Will be the same as the name if there is no variation.
   */
  canonicalName: string;
  /**
   * Confirmation mode for the tool
   */
  confirm?: string | undefined;
  /**
   * Prompt for the confirmation
   */
  confirmPrompt?: string | undefined;
  /**
   * The creation date of the tool.
   */
  createdAt: Date;
  /**
   * The ID of the deployment
   */
  deploymentId: string;
  /**
   * Description of the tool
   */
  description: string;
  /**
   * The ID of the function
   */
  functionId: string;
  /**
   * The ID of the tool
   */
  id: string;
  /**
   * Meta tags for the tool
   */
  meta?: { [k: string]: any } | undefined;
  /**
   * The name of the tool
   */
  name: string;
  /**
   * The ID of the project
   */
  projectId: string;
  /**
   * Runtime environment (e.g., nodejs:24, python:3.12)
   */
  runtime: string;
  /**
   * JSON schema for the request
   */
  schema: string;
  /**
   * Version of the schema
   */
  schemaVersion?: string | undefined;
  /**
   * Summarizer for the tool
   */
  summarizer?: string | undefined;
  /**
   * The tags list for this function tool
   */
  tags: Array<string>;
  /**
   * The URN of this tool
   */
  toolUrn: string;
  /**
   * The last update date of the tool.
   */
  updatedAt: Date;
  /**
   * Variables configuration for the function
   */
  variables?: any | undefined;
  variation?: ToolVariation | undefined;
};

/** @internal */
export const FunctionToolDefinition$inboundSchema: z.ZodMiniType<
  FunctionToolDefinition,
  unknown
> = z.pipe(
  z.object({
    annotations: z.optional(ToolAnnotations$inboundSchema),
    asset_id: z.string(),
    canonical: z.optional(CanonicalToolAttributes$inboundSchema),
    canonical_name: z.string(),
    confirm: z.optional(z.string()),
    confirm_prompt: z.optional(z.string()),
    created_at: z.pipe(
      z.iso.datetime({ offset: true }),
      z.transform(v => new Date(v)),
    ),
    deployment_id: z.string(),
    description: z.string(),
    function_id: z.string(),
    id: z.string(),
    meta: z.optional(z.record(z.string(), z.any())),
    name: z.string(),
    project_id: z.string(),
    runtime: z.string(),
    schema: z.string(),
    schema_version: z.optional(z.string()),
    summarizer: z.optional(z.string()),
    tags: z.array(z.string()),
    tool_urn: z.string(),
    updated_at: z.pipe(
      z.iso.datetime({ offset: true }),
      z.transform(v => new Date(v)),
    ),
    variables: z.optional(z.any()),
    variation: z.optional(ToolVariation$inboundSchema),
  }),
  z.transform((v) => {
    return remap$(v, {
      "asset_id": "assetId",
      "canonical_name": "canonicalName",
      "confirm_prompt": "confirmPrompt",
      "created_at": "createdAt",
      "deployment_id": "deploymentId",
      "function_id": "functionId",
      "project_id": "projectId",
      "schema_version": "schemaVersion",
      "tool_urn": "toolUrn",
      "updated_at": "updatedAt",
    });
  }),
);

export function functionToolDefinitionFromJSON(
  jsonString: string,
): SafeParseResult<FunctionToolDefinition, SDKValidationError> {
  return safeParse(
    jsonString,
    (x) => FunctionToolDefinition$inboundSchema.parse(JSON.parse(x)),
    `Failed to parse 'FunctionToolDefinition' from JSON`,
  );
}
